import { useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { productList, productListVariables } from 'src/queries/__generated__/productList';
import { ProductGalleryContext } from 'src/components/ProductDetail/ProductGallery';

import productListQuery from '../../queries/productListNoImages.graphql';
import productListMediaGalleryQuery from '../../queries/productListMediaGallery.graphql';

import { ProductOptionsContext } from './ProductOptionsProvider';
import { Size } from './ProductSizes';
import { appendStockToSize } from './utils/selectVariantsFromOptions';
import { hydrateBaseVariants, ProductVariant } from './utils/product-detail.types';

export function useVariantQuery() {
    const { baseVariants, variant, sizes } = useContext(ProductOptionsContext);
    const { setMediaEntries } = useContext(ProductGalleryContext);

    const { loading, data, error } = useQuery<productList, productListVariables>(productListQuery, {
        variables: { skus: sizes ? sizes?.map((x) => x.simpleProductSku!) : [] },
        ssr: false,
    });

    const { data: mediaGallery } = useQuery<productList, productListVariables>(productListMediaGalleryQuery, {
        variables: { skus: sizes ? sizes?.map((x) => x.simpleProductSku!) : [] },
        ssr: false,
    });

    /**
     * Choose a color variant if it's selected
     */
    const variantColorIndex = variant?.attributes.find((x) => x.code === 'color')?.value_index;

    /**
     * Determine the initial state of the form
     */
    const initialValues = useMemo(() => {
        if (variant && variantColorIndex) {
            return { color: variantColorIndex };
        }
        return {};
    }, [variant, variantColorIndex]);

    /**
     * Hydrated version of the selected variants
     */
    const hydratedVariantsNoMedia: ProductVariant[] = useMemo(
        () => hydrateBaseVariants(baseVariants, data),
        [data, baseVariants],
    );

    /**
     * This will add stock information to sizes, given the idea that a color
     * is always pre-selected.
     *
     * Note: This may need optimizing if we see any issues with UI performance.
     */
    const computedSizes = useMemo(() => {
        if (!hydratedVariantsNoMedia) return sizes;
        if (!data || loading) return sizes;
        const output: Size[] = [];
        sizes.forEach((size) => {
            const nextSize = appendStockToSize(size, initialValues, hydratedVariantsNoMedia!);
            /**
             * If the `simpleProductId` is NOT -1, that means that
             * stock information was obtained and this size should be shown
             */
            if (nextSize.simpleProductSku) {
                output.push(nextSize);
            }
        });

        return output;
    }, [hydratedVariantsNoMedia, sizes, data, loading, initialValues]);

    const hydratedVariants: ProductVariant[] = useMemo(() => {
        const dataWithMedia = mediaGallery && {
            productList: data?.productList.map((dataItem) => {
                const matchingItem = mediaGallery?.productList.find(
                    (galleryItem) => galleryItem?.sku === dataItem?.sku,
                );
                return {
                    ...dataItem,
                    media_gallery: matchingItem?.media_gallery,
                    small_image: matchingItem?.small_image,
                };
            }),
        };

        return hydrateBaseVariants(baseVariants, dataWithMedia as productList);
    }, [baseVariants, data?.productList, mediaGallery]);

    useEffect(() => {
        const firstHydratedChild = hydratedVariants[0];

        if (firstHydratedChild) {
            setMediaEntries(firstHydratedChild.media_entries);
        }
    }, [hydratedVariants, setMediaEntries]);

    return { computedSizes, loading, initialValues, variant, hydratedVariants, error, data };
}
